import { TCompany } from "@/api/entities/company.entity";
import { TMatch } from "@/api/entities/matches.entity";
import { TGetMeDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { FCC } from "@/types/common";
import { createContext, MouseEventHandler, useContext, useMemo } from "react";
import { matchSchema } from "@/schemas/matchSchema";
import { useVisible } from "@/hooks/useVisible";
import { TUserType } from "@/types/enum";
import { useCurrentUserQuery, CurrentUser } from "@/infrastructure/adapters/current-user";

export type InitSetupType = {
  currentStep: number;
  checkFinishInitStep: MouseEventHandler<HTMLElement>;
  isLoading: boolean;
  userType: TUserType;
  user?: TGetMeDto;
  match?: TMatch;
  company?: TCompany;
};

export const InitSetupContext = createContext<InitSetupType | undefined>(undefined);

/**
 * Map CurrentUser to TGetMeDto format for backwards compatibility
 */
const mapCurrentUserToDto = (currentUser: CurrentUser | undefined): TGetMeDto | undefined => {
  if (!currentUser) return undefined;
  return {
    id: currentUser.id,
    fullName: currentUser.fullName,
    email: currentUser.email,
    type: currentUser.userType,
    rsoId: currentUser.rsoId,
    companyId: currentUser.companyId,
    match: currentUser.match,
    company: currentUser.company,
    isUser: currentUser.isUser,
  };
};

export const InitSetupProvider: FCC = ({ children }) => {
  const matchInfoStep = useVisible();

  const { data: currentUser, isLoading } = useCurrentUserQuery();

  const user = useMemo(() => mapCurrentUserToDto(currentUser), [currentUser]);

  const userType: TUserType = currentUser?.userType ?? "rso_user";

  const match = useMemo(() => currentUser?.match, [currentUser]);
  const company = useMemo(() => currentUser?.company, [currentUser]);

  const isPassMatchInfoStep = useMemo(() => {
    if (userType === "company_user") return true;

    if (!match) return false;
    const compareKeys: (keyof TMatch)[] = [];
    Object.entries(matchSchema.fields).forEach(([key, value]) => {
      const fieldDes = value.describe();
      if ("optional" in fieldDes && fieldDes.optional === false && "nullable" in fieldDes && fieldDes.nullable === false) {
        compareKeys.push(key as keyof TMatch);
      }
    });
    const isFullFillRso = !compareKeys.some((key) => match[key] === null || match[key] === undefined || match[key] === "");

    return isFullFillRso;
  }, [match, userType]);

  const currentStep = useMemo(() => {
    if (!isPassMatchInfoStep) return 1;
    return 5;
  }, [isPassMatchInfoStep]);

  const checkFinishInitStep: MouseEventHandler<HTMLElement> = (e) => {
    if (!isPassMatchInfoStep) {
      e.preventDefault();
      matchInfoStep.open();
      return;
    }
  };

  return (
    <InitSetupContext.Provider
      value={{
        user,
        match,
        company,
        currentStep,
        checkFinishInitStep,
        isLoading,
        userType,
      }}
    >
      {children}
    </InitSetupContext.Provider>
  );
};

export const useInitSetup = (): InitSetupType => {
  const context = useContext(InitSetupContext);
  if (!context) {
    throw new Error("useInitSetup must be used within a InitSetupProvider");
  }
  return context;
};
